/** ONE CONNECT — Public Reference & Verification sources (Phase 2). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Globe2, RefreshCw } from "lucide-react";

const TABS = ["PROVIDERS", "VERIFY ENTITY", "VAT", "DOMAINS & DNS", "LIFECYCLE", "ENRICHMENTS"];
const inputCls = "text-[11px] border border-slate-300 rounded-md px-2 py-1.5";
const btn = "text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50";
const ST = { AVAILABLE: "bg-emerald-100 text-emerald-800", SETUP_REQUIRED: "bg-amber-100 text-amber-800",
  DISABLED: "bg-slate-200 text-slate-600", OK: "bg-emerald-100 text-emerald-800",
  SOURCE_TEMPORARILY_UNAVAILABLE: "bg-amber-100 text-amber-800",
  VERIFIED_SOURCE_MATCH: "bg-emerald-100 text-emerald-800", POSSIBLE_MATCH: "bg-amber-100 text-amber-800",
  NO_LEI_FOUND: "bg-slate-200 text-slate-600", REVIEW_REQUIRED: "bg-orange-100 text-orange-800",
  VALID: "bg-emerald-100 text-emerald-800", INVALID: "bg-rose-100 text-rose-800",
  SERVICE_UNAVAILABLE: "bg-amber-100 text-amber-800", HEALTHY: "bg-emerald-100 text-emerald-800",
  NOT_CONFIGURED: "bg-slate-200 text-slate-600", UNKNOWN: "bg-slate-200 text-slate-600",
  SUPPORTED: "bg-emerald-100 text-emerald-800", SUPPORT_ENDING: "bg-amber-100 text-amber-800",
  EOL: "bg-rose-100 text-rose-800" };
const Pill = ({ v }) => <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
  (ST[v] || "bg-slate-100 text-slate-600")}>{(v || "").replace(/_/g, " ")}</span>;
const fmt = (iso) => (iso || "").replace("T", " ").slice(0, 16);

export default function PublicReferenceSources() {
  const [tab, setTab] = useState("PROVIDERS");
  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="public-ref-page">
      <header className="flex items-center gap-3 bg-sky-950 text-white rounded-xl px-4 py-3">
        <div className="h-10 w-10 rounded-lg bg-sky-400 text-sky-950 grid place-items-center">
          <Globe2 className="h-5 w-5" /></div>
        <div>
          <div className="text-lg font-heading font-semibold">PUBLIC REFERENCE SOURCES</div>
          <div className="text-[10.5px] text-sky-200/80">GLEIF · VIES · RDAP · endoflife.date · Companies House · Public DNS — reference evidence, never overwrites approved data</div>
        </div>
      </header>
      <div className="flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
              data-testid={`pr-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={"text-[10.5px] px-3 py-1.5 rounded-full border " +
                (tab === t ? "bg-sky-950 text-white border-sky-950" : "border-slate-300")}>{t}</button>))}
      </div>
      {tab === "PROVIDERS" && <Providers />}
      {tab === "VERIFY ENTITY" && <VerifyEntity />}
      {tab === "VAT" && <Vat />}
      {tab === "DOMAINS & DNS" && <DomainHealthSection />}
      {tab === "LIFECYCLE" && <Lifecycle />}
      {tab === "ENRICHMENTS" && <Enrichments />}
    </div>);
}

function Providers() {
  const [d, setD] = useState(null);
  const [chKey, setChKey] = useState("");
  const load = useCallback(() => api.get("/public-ref/providers").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const saveKey = async () => {
    await api.put("/public-ref/config", { companies_house_api_key: chKey });
    toast.success("Companies House key saved (server-side, never displayed)"); setChKey(""); load();
  };
  return (
    <div className="space-y-3" data-testid="pr-providers">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {d.items.map((p) => (
          <div key={p.provider} className="border border-slate-200 rounded-xl bg-white p-3"
              data-testid={`pr-provider-${p.provider}`}>
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold flex-1">{p.label}</span>
              <Pill v={p.status} /></div>
            <div className="mt-1">
              <span className="inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                data-testid={`pr-access-${p.provider}`}>
                {(p.access_mode || "PUBLIC_NO_KEY").replaceAll("_", " ")}
              </span>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5 space-y-0.5">
              <div>Source: {p.source}</div>
              <div>Last success: {fmt(p.last_success) || "—"}</div>
              <div>Enriched: {p.enriched} · Errors: {p.errors}</div>
              <a href={p.docs} target="_blank" rel="noreferrer" className="text-sky-800 underline">Documentation</a>
            </div>
          </div>))}
      </div>
      {!d.companies_house_key_set && (
        <div className="border border-amber-300 bg-amber-50 rounded-xl p-3 flex items-center gap-2">
          <span className="text-[11px] flex-1">UK Companies House needs a free API key
            (developer.company-information.service.gov.uk):</span>
          <input value={chKey} onChange={(e) => setChKey(e.target.value)} placeholder="API key"
              data-testid="pr-ch-key" className={inputCls + " w-64"} />
          <button onClick={saveKey} disabled={!chKey} data-testid="pr-ch-save" className={btn}>Save</button>
        </div>)}
      <ConfigRow d={d} onSaved={load} />
    </div>);
}

function ConfigRow({ d, onSaved }) {
  const [domains, setDomains] = useState((d.config.domains || []).join(", "));
  const [watch, setWatch] = useState((d.config.tech_watchlist || []).join(", "));
  const save = async () => {
    await api.put("/public-ref/config", {
      domains: domains.split(",").map((s) => s.trim()).filter(Boolean),
      tech_watchlist: watch.split(",").map((s) => s.trim()).filter(Boolean) });
    toast.success("Configuration saved"); onSaved();
  };
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-3 grid md:grid-cols-2 gap-3" data-testid="pr-config">
      <label className="text-[10.5px]">Corporate domains (DNS health + RDAP renewals)
        <input value={domains} onChange={(e) => setDomains(e.target.value)}
            data-testid="pr-cfg-domains" className={inputCls + " w-full mt-1"} /></label>
      <label className="text-[10.5px]">Technology watchlist (endoflife.date slugs)
        <input value={watch} onChange={(e) => setWatch(e.target.value)}
            data-testid="pr-cfg-watchlist" className={inputCls + " w-full mt-1"} /></label>
      <div className="md:col-span-2 text-right">
        <button onClick={save} data-testid="pr-cfg-save" className={btn}>Save configuration</button></div>
    </div>);
}

function VerifyEntity() {
  const [name, setName] = useState(""); const [country, setCountry] = useState("");
  const [lei, setLei] = useState(""); const [r, setR] = useState(null);
  const [chQ, setChQ] = useState(""); const [ch, setCh] = useState(null);
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/public-ref/gleif/verify",
        { params: lei ? { lei } : { name, country: country || undefined } });
      setR(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const searchCh = async () => {
    const { data } = await api.get("/public-ref/companies-house/search", { params: { q: chQ } });
    setCh(data);
  };
  const attach = async (m) => {
    await api.post("/public-ref/enrichments", { provider: "GLEIF", entity_type: "LEGAL_ENTITY",
      entity_id: m.lei || m.legal_name, match_status: r.match_status, match_method: r.match_method,
      current_value: name || lei, proposed_value: m.legal_name, record: m });
    toast.success("Attached as enrichment evidence (master data unchanged)");
  };
  return (
    <div className="space-y-4" data-testid="pr-verify-entity">
      <div className="border border-slate-200 rounded-xl bg-white p-3 space-y-2">
        <div className="text-[11px] font-semibold">VERIFY LEGAL ENTITY — GLEIF LEI</div>
        <div className="flex flex-wrap gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Legal name"
              data-testid="pr-gleif-name" className={inputCls + " w-64"} />
          <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (IN, US…)"
              data-testid="pr-gleif-country" className={inputCls + " w-32"} />
          <input value={lei} onChange={(e) => setLei(e.target.value)} placeholder="…or exact LEI"
              data-testid="pr-gleif-lei" className={inputCls + " w-56"} />
          <button onClick={verify} disabled={busy || (!name && !lei)} data-testid="pr-gleif-verify"
              className={btn}>{busy ? "Checking…" : "Verify Legal Entity"}</button>
        </div>
        {r && (
          <div className="space-y-1.5" data-testid="pr-gleif-result">
            <div className="text-[11px]"><Pill v={r.match_status} />
              <span className="text-slate-400 ml-2">method {r.match_method} · retrieved {fmt(r.retrieved_at)}
                {r.source_status !== "OK" && <span className="text-amber-700 ml-1">SOURCE TEMPORARILY UNAVAILABLE</span>}</span></div>
            {r.matches.map((m) => (
              <div key={m.lei} className="border border-slate-100 rounded-lg px-3 py-2 text-[11px] flex items-start gap-2">
                <div className="flex-1">
                  <b>{m.legal_name}</b> · LEI <span className="font-mono">{m.lei}</span>
                  <div className="text-[10px] text-slate-500">{m.address} · {m.jurisdiction} ·
                    entity {m.entity_status} · registration {m.registration_status} · source updated {fmt(m.source_updated)}</div>
                </div>
                <button onClick={() => attach(m)} data-testid={`pr-gleif-attach-${m.lei}`}
                    className="text-[9.5px] px-2 py-1 rounded border border-slate-300">Attach evidence</button>
              </div>))}
            {r.match_status === "NO_LEI_FOUND" &&
              <div className="text-[10.5px] text-slate-500">No LEI found — absence of an LEI does not make a company invalid.</div>}
          </div>)}
      </div>
      <div className="border border-slate-200 rounded-xl bg-white p-3 space-y-2">
        <div className="text-[11px] font-semibold">VERIFY UK COMPANY — Companies House</div>
        <div className="flex gap-2">
          <input value={chQ} onChange={(e) => setChQ(e.target.value)} placeholder="Company name or number"
              data-testid="pr-ch-q" className={inputCls + " w-72"} />
          <button onClick={searchCh} disabled={!chQ} data-testid="pr-ch-search" className={btn}>Verify UK Company</button>
        </div>
        {ch && (ch.status === "SETUP_REQUIRED"
          ? <div className="text-[11px] text-amber-700" data-testid="pr-ch-setup">SETUP REQUIRED — {ch.message}</div>
          : <div className="space-y-1" data-testid="pr-ch-results">
              {(ch.items || []).map((i) => (
                <div key={i.company_number} className="border border-slate-100 rounded-lg px-3 py-2 text-[11px]">
                  <b>{i.title}</b> · <span className="font-mono">{i.company_number}</span> · {i.status} · {i.type}
                  <div className="text-[10px] text-slate-500">{i.address} · incorporated {i.incorporated}</div>
                </div>))}
            </div>)}
      </div>
    </div>);
}

function Vat() {
  const [country, setCountry] = useState("DE"); const [vat, setVat] = useState("");
  const [r, setR] = useState(null); const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try { const { data } = await api.get("/public-ref/vies/verify", { params: { country, vat } }); setR(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-3 space-y-2 max-w-xl" data-testid="pr-vat">
      <div className="text-[11px] font-semibold">VERIFY VAT — EU VIES (EU / Northern Ireland only, never GSTIN)</div>
      <div className="flex gap-2">
        <input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())}
            data-testid="pr-vies-country" className={inputCls + " w-20"} maxLength={2} />
        <input value={vat} onChange={(e) => setVat(e.target.value)} placeholder="VAT number"
            data-testid="pr-vies-vat" className={inputCls + " w-56"} />
        <button onClick={verify} disabled={busy || !vat} data-testid="pr-vies-verify" className={btn}>Verify VAT</button>
      </div>
      {r && (
        <div className="text-[11px] space-y-0.5" data-testid="pr-vies-result">
          <div><Pill v={r.result} /> <span className="font-mono">{r.country} {r.vat}</span></div>
          {r.returned_name && <div>Name: {r.returned_name}</div>}
          {r.returned_address && <div className="text-slate-500">Address: {r.returned_address}</div>}
          <div className="text-[10px] text-slate-400">Verified {fmt(r.verified_at)} · ref {r.reference || "—"} · source {r.source}</div>
        </div>)}
    </div>);
}

export function DomainHealthSection() {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState("");
  const [rdap, setRdap] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/public-ref/domain-health", { params: force ? { force: true } : {} });
      setItems(data.items || []); setChecked(data.checked_at);
      for (const it of data.items || []) {
        api.get("/public-ref/rdap/domain", { params: { domain: it.domain } })
          .then(({ data: rd }) => setRdap((p) => ({ ...(p || {}), [it.domain]: rd }))).catch(() => {});
      }
    } catch { toast.error("DNS check failed"); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="pr-domain-health">
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-semibold flex-1">DOMAIN & EMAIL HEALTH
          <span className="text-slate-400 font-normal"> · Google Public DNS + RDAP · last checked {fmt(checked)}</span></div>
        <button onClick={() => load(true)} disabled={busy} data-testid="pr-dns-refresh"
            className={btn + " inline-flex items-center gap-1"}><RefreshCw className="h-3 w-3" /> Refresh now</button>
      </div>
      {items.map((d) => {
        const rd = rdap?.[d.domain];
        return (
          <div key={d.domain} className="border border-slate-200 rounded-xl bg-white p-3" data-testid={`pr-domain-${d.domain}`}>
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold font-mono flex-1">{d.domain}</span>
              <Pill v={d.status} /></div>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-[10.5px]">
              <div>Resolution: {d.resolution ? "✓ resolves" : "✗ no A record"}</div>
              <div>MX: {d.mx.length ? d.mx.slice(0, 2).join(", ") : "none"}</div>
              <div>SPF: {d.spf ? "✓ present" : "not identifiable"}</div>
              <div>DMARC: {d.dmarc ? "✓ present" : "not identifiable"}</div>
              <div>CAA: {d.caa.length ? d.caa.join(", ") : "none published"}</div>
              <div>NS: {d.nameservers.slice(0, 2).join(", ") || "—"}</div>
              {rd && <div className="md:col-span-2 text-slate-500">
                RDAP: registrar {rd.summary?.registrar || "—"} · expires {fmt(rd.summary?.expiration) || "not exposed"}
                {rd.source_status !== "OK" && " · SOURCE TEMPORARILY UNAVAILABLE"}
                {rd.summary?.expiration && <span className="text-emerald-700"> · fed to Renewal Centre</span>}</div>}
            </div>
          </div>);
      })}
      <div className="text-[9.5px] text-slate-400">Statements reflect actual DNS answers only — no security claims beyond DNS.</div>
    </div>);
}

function Lifecycle() {
  const [d, setD] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/public-ref/lifecycle").then(({ data }) => setD(data)); }, []);
  const open = async (p) => {
    const { data } = await api.get("/public-ref/eol/product", { params: { name: p } });
    setDetail(data);
  };
  if (!d) return <div className="text-[11px] text-slate-400">Loading lifecycle intelligence…</div>;
  return (
    <div className="space-y-3" data-testid="pr-lifecycle">
      <div className="text-[10.5px] text-amber-800 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2">
        endoflife.date is a REFERENCE SOURCE — VERIFY WITH OEM for high-impact lifecycle decisions.</div>
      <div className="grid md:grid-cols-3 gap-2">
        {d.items.map((t) => (
          <button key={t.product} onClick={() => open(t.product)} data-testid={`pr-eol-${t.product}`}
              className="border border-slate-200 rounded-xl bg-white p-3 text-left hover:border-sky-600">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold flex-1">{t.product}</span><Pill v={t.status} /></div>
            <div className="text-[10px] text-slate-500 mt-1">
              Latest cycle {t.latest?.cycle || "—"} · EOL {String(t.latest?.eol ?? "—")}</div>
          </button>))}
      </div>
      {detail && (
        <div className="border border-slate-200 rounded-xl bg-white p-3" data-testid="pr-eol-detail">
          <div className="text-[11px] font-semibold mb-2">{detail.product} — lifecycle cycles
            {detail.source_status !== "OK" && <span className="text-amber-700 ml-2">SOURCE TEMPORARILY UNAVAILABLE</span>}</div>
          <table className="w-full text-[10.5px]">
            <thead className="text-slate-500"><tr>
              <th className="text-left py-1">Cycle</th><th className="text-left">Released</th>
              <th className="text-left">Active support</th><th className="text-left">EOL</th>
              <th className="text-left">Latest</th><th className="text-left">Status</th></tr></thead>
            <tbody>{detail.cycles.map((c) => (
              <tr key={c.cycle} className="border-t border-slate-100">
                <td className="py-1 font-mono">{c.cycle}</td><td>{c.release_date}</td>
                <td>{String(c.support ?? "—")}</td><td>{String(c.eol ?? "—")}</td>
                <td className="font-mono">{c.latest}</td><td><Pill v={c.status} /></td></tr>))}</tbody>
          </table>
        </div>)}
    </div>);
}

function Enrichments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public-ref/enrichments").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="pr-enrichments">
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          {["Provider", "Entity", "Match", "Method", "Current", "Proposed", "Retrieved", "By"].map((c) =>
            <th key={c} className="text-left px-3 py-2">{c}</th>)}</tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 px-3 py-8">
            No enrichment evidence attached yet — provenance appears here (approved data is never silently replaced).</td></tr>}
          {items.map((e) => (
            <tr key={e.id} className="border-t border-slate-100">
              <td className="px-3 py-1.5 font-semibold">{e.provider}</td>
              <td className="px-3 py-1.5">{e.entity_type}</td>
              <td className="px-3 py-1.5"><Pill v={e.match_status} /></td>
              <td className="px-3 py-1.5 text-[10px]">{e.match_method}</td>
              <td className="px-3 py-1.5">{e.current_value || "—"}</td>
              <td className="px-3 py-1.5">{e.proposed_value || "—"}</td>
              <td className="px-3 py-1.5 text-[10px]">{fmt(e.retrieved_at)}</td>
              <td className="px-3 py-1.5 text-[10px] text-slate-500">{e.attached_by}</td>
            </tr>))}
        </tbody>
      </table>
    </div>);
}
